import { classnames } from '../../utils/classnames'
import React, { useContext, useEffect, useRef, useState } from 'react'
import Flatpickr from 'react-flatpickr'

import formatDate from '../../utils/formatDate'

export default function FormDate({
    value,
    onChange,
    config = {},
    className = '',
    id = Math.random(),
    hideInput,
    disabled,
    light,
    hoverLight,
    borderless,
    plaintext,
    // ignoreGlobalDisable,
    title,
    inline,
    disableWeekends,
    disablePastDates,
    disableToday,
    calendarIcon = true,
    calendarIconPosition = 'end',
    placeholder = '    /       /',
    style,
    ...flatpickrProps
}) {
    // const { disableAll } = useContext(GlobalFormContext)

    const parentRef = useRef(null)
    // const parent = parentRef.current

    const fpRef = useRef(null)

    // Function to validate if the date input is complete and valid
    const isValidDate = (dateString) => {
        if (!dateString || dateString.trim() === '') return false

        // Check if the date string matches the expected format (m/d/yyyy or mm/dd/yyyy)
        const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/
        if (!dateRegex.test(dateString)) return false

        // Try to parse the date and check if it's valid
        const parts = dateString.split('/')
        const month = parseInt(parts[0], 10)
        const day = parseInt(parts[1], 10)
        const year = parseInt(parts[2], 10)

        // Create a date object and check if it's valid
        const date = new Date(year, month - 1, day)
        return (
            date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
        )
    }

    // Function to format input with automatic slash insertion
    const formatDateInput = (value) => {
        // Remove all non-numeric characters
        const numericValue = value.replace(/\D/g, '')

        // Format based on length
        if (numericValue.length <= 2) {
            return numericValue
        } else if (numericValue.length <= 4) {
            return `${numericValue.slice(0, 2)}/${numericValue.slice(2)}`
        } else {
            return `${numericValue.slice(0, 2)}/${numericValue.slice(2, 4)}/${numericValue.slice(
                4,
                8
            )}`
        }
    }

    // Handle input changes with automatic formatting (but don't trigger onChange while typing)
    const handleInputChange = (e) => {
        const cursorPosition = e.target.selectionStart
        const inputValue = e.target.value
        const formattedValue = formatDateInput(inputValue)

        // Update the input value
        e.target.value = formattedValue

        // Adjust cursor position after formatting
        let newCursorPosition = cursorPosition
        if (inputValue.length < formattedValue.length) {
            // A slash was added, move cursor forward
            newCursorPosition = cursorPosition + (formattedValue.length - inputValue.length)
        }

        // Set cursor position after a brief delay to ensure the value is updated
        setTimeout(() => {
            if (e.target === document.activeElement) {
                e.target.setSelectionRange(newCursorPosition, newCursorPosition)
            }
        }, 0)

        // DON'T trigger onChange while typing - wait for blur or calendar selection
        // This prevents the parent component from updating the value while user is still typing
    }

    let fpConfig = {
        allowInput: !disabled,
        dateFormat: 'm/d/Y',
        position: 'auto left',
        static: true,
        inline: inline,
        disable: config?.disable || [],
        minDate: disablePastDates ? 'today' : null,
        plugins: [],
        onReady: function (selectedDates, dateStr, instance) {
            // Add input event listener for automatic slash insertion
            instance.input.addEventListener('input', handleInputChange)
        },
        onDestroy: function (selectedDates, dateStr, instance) {
            // Clean up event listener
            if (instance.input) {
                instance.input.removeEventListener('input', handleInputChange)
            }
        },
        ...config,
    }

    if (disableToday) {
        fpConfig.disable.push(new Date())
    }
    if (disableWeekends) {
        fpConfig.disable.push((date) => date.getDay() === 0 || date.getDay() === 6)
    }

    // Handle onBlur to trigger onChange for manually typed dates
    useEffect(() => {
        const handleBlur = (e) => {
            // Check if the blur is happening because user clicked inside the calendar
            // If so, don't process the blur - let the calendar handle the selection
            const relatedTarget = e.relatedTarget
            const calendarEl = fpRef.current?.flatpickr?.calendarContainer
            if (calendarEl && (calendarEl.contains(relatedTarget) || calendarEl === relatedTarget)) {
                return
            }

            // Also check if calendar is still open - if so, don't process blur yet
            if (fpRef.current?.flatpickr?.isOpen) {
                return
            }

            const inputValue = e.target.value.trim()
            const currentFormattedValue = formatDate(value)

            // Only process if the input value is different from the current value
            if (inputValue !== currentFormattedValue) {
                // Checking if the input value is a valid date, could be typed manually
                if (isValidDate(inputValue)) {
                    // Parse the date and trigger onChange
                    const parts = inputValue.split('/')
                    const month = parseInt(parts[0], 10) - 1 // Month is 0-indexed in Date constructor
                    const day = parseInt(parts[1], 10)
                    const year = parseInt(parts[2], 10)
                    const dateObj = new Date(year, month, day)

                    // Call onChange with the parsed date
                    onChange([dateObj], inputValue, fpRef.current?.flatpickr)

                    // Update flatpickr's internal date without triggering events again
                    fpRef.current.flatpickr.setDate(dateObj, false)
                } else if (inputValue === '') {
                    // Handle empty input - clear the date
                    onChange([], '', fpRef.current?.flatpickr)
                    fpRef.current.flatpickr.clear(false)
                } else {
                    // Invalid date - revert to previous value
                    fpRef.current.flatpickr.setDate(value, false)
                }
            }
        }

        const element = fpRef.current?.node
        if (!element) return

        element.addEventListener('blur', handleBlur)

        return () => element.removeEventListener('blur', handleBlur)
    }, [value, onChange])

    const isDisabled = disabled // || (disableAll && !ignoreGlobalDisable)

    return (
		!isDisabled ? (
			<span ref={parentRef} id={id} className='position-relative'>
				<Flatpickr
					{...flatpickrProps}
					ref={fpRef}
					value={formatDate(value)}
					options={fpConfig}
					onChange={onChange}
					placeholder={placeholder}
					className={classnames('form-control', 'js-input-mask', className, {
						'flatpickr-hide-input': hideInput,
						'form-control-light': light,
						'form-control-borderless': borderless,
						'form-control-hover-light': hoverLight,
						'form-control-title': title,
						'form-control-plaintext': plaintext,
					})}
					onFocus={(e) => e.target.select()}
					disabled={isDisabled}
					style={{
						...style,
						paddingLeft: calendarIcon && calendarIconPosition == 'start' ? '1.75rem' : null,
						paddingRight: calendarIcon && calendarIconPosition == 'end' ? '1.75rem' : null,
					}}
				/>
				{isDisabled && inline && (
					<div
						className='position-absolute'
						style={{ 
							top: 0, 
							left: 0, 
							right: 0, 
							bottom: 0, 
							backgroundColor: '#fff',
							zIndex: 99, 
							opacity: 0.5 
						}}
					/>
				)}
				{calendarIcon && !inline && (
					<div
						className='position-absolute text-muted-2 h-100 d-flex align-items-center'
						style={{
							left: calendarIconPosition == 'start' ? 11 : null,
							right: calendarIconPosition == 'end' ? 11 : null,
							top: 0,
							cursor: !isDisabled ? 'pointer' : 'default',
						}}
						onClick={() => (!isDisabled ? fpRef.current.flatpickr.open() : null)}
					>
						<i className='far fa-calendar fa-xs text-muted-2 d-inline-block align-middle' />
					</div>
				)}
			</span>
		) : (
			<span>{formatDate(value)}</span>
		)
    )
}
